import { useState } from "react";
import { Check, FolderClosed, FolderMinus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useMoveConversation, useProjects } from "@/hooks/queries";
import { cn } from "@/lib/utils";

export function MoveToProjectDialog({ conversation, onClose }) {
  const { data } = useProjects("active");
  const move = useMoveConversation();
  const [target, setTarget] = useState(null);
  const current = conversation?.project_id || null;
  const pick = async (projectId) => {
    setTarget(projectId);
    try {
      await move.mutateAsync({ id: conversation.id, projectId });
      toast.success(projectId ? "Moved to project" : "Removed from project");
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const Row = ({ id, label, icon: Icon }) => (
    <button type="button" disabled={move.isPending} onClick={() => pick(id)} data-testid={`move-to-project-${id || "none"}`} className={cn("flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/[0.05]", current === id && "bg-white/[0.06]")}>
      <Icon className="h-4 w-4 text-zinc-400" strokeWidth={1.6} />
      <span className="min-w-0 flex-1 truncate text-zinc-100">{label}</span>
      {current === id && <Check className="h-4 w-4 text-zinc-300" />}
      {move.isPending && target === id && <span className="text-xs text-zinc-500">Moving…</span>}
    </button>
  );
  return (
    <Dialog open={Boolean(conversation)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-ink-850 sm:max-w-md" data-testid="move-to-project-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Move to project</DialogTitle>
        </DialogHeader>
        <div className="mt-2 max-h-[360px] space-y-0.5 overflow-y-auto">
          {(data?.items || []).map((p) => <Row key={p.id} id={p.id} label={p.name} icon={FolderClosed} />)}
          {data && !data.items.length && <p className="px-3 py-6 text-center text-sm text-zinc-500">No projects yet. Create one from Projects.</p>}
          {current && <Row id={null} label="Remove from project" icon={FolderMinus} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
